// Accept-invite page — where the emailed link lands. Validates the token, then
// lets the new joiner choose their own password. No password is ever emailed,
// so this is the first and only place one is set.
//
// After success they go to /login; the existing first-login gate then routes
// them into the onboarding wizard as usual.
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";
import { API_URL } from "../../api/api";
import emLogo from "../../images/em-logo.svg";
import "../Onboarding/onboarding.css";

const MIN_PASSWORD_LENGTH = 8;

// Reveal toggle, mirroring PwdInput in Settings/PasswordManagement. Declared at
// module level on purpose: defined inside AcceptInvite it would be a new
// component type every render, remounting the input and losing focus per
// keystroke. Styling rides on `.ob-field input` from onboarding.css; only the
// right padding is overridden so typed text can't run under the icon.
const PasswordInput = ({ id, value, onChange, placeholder, show, onToggle }) => (
  <div style={{ position: "relative", display: "flex" }}>
    <input
      id={id}
      type={show ? "text" : "password"}
      autoComplete="new-password"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{ width: "100%", boxSizing: "border-box", paddingRight: 40 }}
    />
    <button
      type="button"
      onClick={onToggle}
      // Not a submit button — inside a <form> an untyped button would submit.
      aria-label={show ? "Hide password" : "Show password"}
      title={show ? "Hide password" : "Show password"}
      style={{
        position: "absolute",
        right: 10,
        top: "50%",
        transform: "translateY(-50%)",
        background: "none",
        border: 0,
        cursor: "pointer",
        color: "#64748b",
        padding: 2,
        display: "flex",
        alignItems: "center",
      }}
    >
      {show ? <EyeOff size={16} /> : <Eye size={16} />}
    </button>
  </div>
);

// Optional: set REACT_APP_HR_EMAIL to make "Contact your HR admin" a mailto
// link. Left unset it stays plain text rather than a link that goes nowhere.
const HR_EMAIL = process.env.REACT_APP_HR_EMAIL || "";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();

  const [state, setState] = useState({ status: "loading" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // Each field reveals independently — matches PasswordManagement.
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!token) return setState({ status: "invalid" });

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/invite/${token}`);
        if (!cancelled) setState({ status: "valid", email: res.data?.email });
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.data?.code;
        setState({ status: code === "ALREADY_ACCEPTED" ? "used" : "invalid" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < MIN_PASSWORD_LENGTH)
      return setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
    if (password !== confirm) return setError("The two passwords don't match.");

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/invite/accept`, {
        token,
        newPassword: password,
      });
      setState({ status: "done" });
      // Brief confirmation, then hand them to the normal sign-in.
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      const data = err?.response?.data;
      setError(
        (typeof data?.error === "string" ? data.error : data?.error?.message) ||
        "Could not set your password. The link may have expired.",
      );
    } finally {
      setSaving(false);
    }
  };

  // The brand mark sits above every state of this card — it's the first
  // em-Hub screen a new joiner ever sees, so it needs to look like ours.
  // The icon row is conditional: the welcome state passes "" and an empty
  // div would leave its 14px margin as a gap under the logo.
  const card = (icon, title, body, extra) => (
    <div className="ob-welcome-bg">
      <div className="ob-welcome-card">
        <img className="ob-welcome-logo" src={emLogo} alt="EM" />
        {icon && (
          <div className="ob-welcome-icon" aria-hidden="true">{icon}</div>
        )}
        <h1 className="ob-welcome-title">{title}</h1>
        <p className="ob-welcome-body">{body}</p>
        {extra}
      </div>
    </div>
  );

  if (state.status === "loading")
    return card("⏳", "One moment…", "Checking your invitation link.");

  if (state.status === "used")
    return card(
      "✅",
      "You're already set up",
      "This invitation has already been used. Just sign in as usual.",
      <button
        type="button"
        className="ob-btn ob-btn--primary"
        onClick={() => navigate("/login")}
      >
        Go to sign in
      </button>,
    );

  if (state.status === "invalid")
    return card(
      "⌛",
      "This link is invalid or has expired",
      "Invitation links are valid for 48 hours. Ask your HR team to send you a fresh one.",
    );

  if (state.status === "done")
    return card(
      "🎉",
      "You're all set",
      "Your password has been saved. Taking you to the sign-in page…",
    );

  return card(
    "",
    "Welcome to EM",
    <>
      Choose a password for{" "}
      {state.email ? <strong>{state.email}</strong> : "your account"} to finish
      setting up.
    </>,
    // .ob-field carries no bottom margin, so the rows and the button would
    // otherwise sit flush against each other — one gap here spaces them all.
    <form
      onSubmit={submit}
      style={{
        marginTop: 20,
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <div className={`ob-field${error ? " has-error" : ""}`}>
        <label htmlFor="ai-password">
          New password <span className="req">*</span>
        </label>
        <PasswordInput
          id="ai-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
          show={showPassword}
          onToggle={() => setShowPassword((v) => !v)}
        />
      </div>

      <div className={`ob-field${error ? " has-error" : ""}`}>
        <label htmlFor="ai-confirm">
          Confirm password <span className="req">*</span>
        </label>
        <PasswordInput
          id="ai-confirm"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Type it again"
          show={showConfirm}
          onToggle={() => setShowConfirm((v) => !v)}
        />
      </div>

      {error && (
        // margin: 0 — the form's flex gap already spaces this.
        <p style={{ color: "#e7000b", font: "400 13px/1.5 inherit", margin: 0 }}>
          {error}
        </p>
      )}

      <button
        type="submit"
        className="ob-btn ob-btn--primary"
        disabled={saving}
        style={{ width: "100%" }}
      >
        {saving ? "Saving…" : "Set password & continue"}
      </button>

      <p
        style={{
          font: "400 12.5px/1.5 inherit",
          color: "var(--em-fg4)",
          textAlign: "center",
          margin: 0,
        }}
      >
        Wrong address?{" "}
        {HR_EMAIL ? (
          <a
            href={`mailto:${HR_EMAIL}`}
            style={{ color: "#f54b01", fontWeight: 500, textDecoration: "none" }}
          >
            Contact your HR admin
          </a>
        ) : (
          <span style={{ color: "#f54b01", fontWeight: 500 }}>
            Contact your HR admin
          </span>
        )}
      </p>
    </form>,
  );
}
